// client/src/neetjee_guru/components/libraryManagement/BookCatalog.jsx
import React, { Component } from "react";
import { Table, Button, Modal, Form, Row, Col, InputGroup, FormControl, Badge, Pagination, Alert } from "react-bootstrap";
import * as Cookies from "es-cookie";
import { GET_BOOKS_PAGINATED } from "./queries";
import { ADD_BOOK, UPDATE_BOOK, DELETE_BOOK, ADD_BOOK_COPY, DELETE_BOOK_COPY, UPDATE_BOOK_COPY } from "./mutations";

class BookCatalog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      books: [],
      searchQuery: "",
      showAddModal: false,
      showEditModal: false,
      showCopiesModal: false,
      selectedBook: null,
      loading: true,
      error: null,

      // Pagination State
      totalCount: 0,
      currentPage: 1,
      pageSize: 10,
      
      // Inline Modal alerts
      modalError: null,
      modalSuccess: null,
      
      // Form fields
      title: "",
      author: "",
      isbn: "",
      category: "",
      language: "English",
      price: 0.0,
      subject: "",
      total_copies: 1,
      rack_location: "General Rack",

      // Copy Management form fields
      newAccessionNo: "",
      newRackLocation: "",
      newCopyStatus: "available",

      // Copy Inline Editing fields
      editingCopyId: null,
      editAccessionNo: "",
      editRackLocation: "",
      editCopyStatus: "available"
    };
  }

  componentDidMount() {
    this.fetchBooks();
  }

  fetchBooks = async () => {
    const { client } = this.props;
    const { currentPage, pageSize, searchQuery } = this.state;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    try {
      this.setState({ loading: true });
      const { data } = await client.query({
        query: GET_BOOKS_PAGINATED,
        variables: {
          institute_id: instId,
          branch_id: branchId,
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
          search: searchQuery
        },
        fetchPolicy: "network-only",
      });
      const result = data.getBooksPaginated || { books: [], totalCount: 0 };
      this.setState({ 
        books: result.books || [], 
        totalCount: result.totalCount || 0,
        loading: false, 
        error: null 
      });
    } catch (err) {
      console.error("Error fetching books:", err);
      this.setState({ error: err.message, loading: false });
    }
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page }, this.fetchBooks);
  };

  handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value) || 10;
    this.setState({ pageSize: size, currentPage: 1 }, this.fetchBooks);
  };

  handleFieldChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value }, () => {
      if (name === "searchQuery") {
        this.setState({ currentPage: 1 }, this.fetchBooks);
      }
    });
  };

  handleNumberChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: parseFloat(value) || 0.0 });
  };

  openAddModal = () => {
    this.setState({
      showAddModal: true,
      modalError: null,
      modalSuccess: null,
      title: "",
      author: "",
      isbn: "",
      category: "",
      language: "English",
      price: 150.0,
      subject: "",
      total_copies: 1,
      rack_location: "General Rack",
    });
  };

  openEditModal = (book) => {
    this.setState({
      showEditModal: true,
      modalError: null,
      modalSuccess: null,
      selectedBook: book,
      title: book.title,
      author: book.author,
      isbn: book.isbn,
      category: book.category,
      language: book.language,
      price: book.price || 0.0,
      subject: book.subject || "",
      total_copies: book.total_copies,
    });
  };

  openCopiesModal = (book) => {
    this.setState({
      showCopiesModal: true,
      modalError: null,
      modalSuccess: null,
      selectedBook: book,
      newAccessionNo: "",
      newRackLocation: "",
      newCopyStatus: "available",
      editingCopyId: null
    });
  };

  handleAddBookSubmit = async (e) => {
    e.preventDefault();
    const { client } = this.props;
    const { title, author, isbn, category, language, price, subject, total_copies, rack_location } = this.state;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    try {
      await client.mutate({
        mutation: ADD_BOOK,
        variables: {
          input: {
            title,
            author,
            isbn,
            category,
            language,
            price: parseFloat(price) || 0.0,
            subject: subject.trim(),
            total_copies: parseInt(total_copies) || 1,
            rack_location: rack_location.trim() || 'General Rack',
            institute_id: instId,
            branch_id: branchId,
          },
        },
      });
      this.setState({ showAddModal: false });
      this.fetchBooks();
      if (this.props.showToast) {
        this.props.showToast(`Book "${title}" added successfully!`, "success");
      }
    } catch (err) {
      this.setState({ modalError: "Error adding book: " + err.message });
    }
  };

  handleEditBookSubmit = async (e) => {
    e.preventDefault();
    const { client } = this.props;
    const { selectedBook, title, author, isbn, category, language, price, subject } = this.state;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    try {
      await client.mutate({
        mutation: UPDATE_BOOK,
        variables: {
          id: selectedBook.id,
          input: {
            title,
            author,
            isbn,
            category,
            language,
            price: parseFloat(price) || 0.0,
            subject: subject.trim(),
            institute_id: instId,
            branch_id: branchId,
          },
        },
      });
      this.setState({ showEditModal: false, selectedBook: null });
      this.fetchBooks();
      if (this.props.showToast) {
        this.props.showToast(`Book details updated successfully!`, "success");
      }
    } catch (err) {
      this.setState({ modalError: "Error updating book: " + err.message });
    }
  };

  handleDeleteBook = async (id) => {
    if (!window.confirm("Are you sure you want to delete this book? This will delete all its physical copies!")) return;
    const { client } = this.props;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    try {
      await client.mutate({
        mutation: DELETE_BOOK,
        variables: { id, institute_id: instId, branch_id: branchId },
      });
      this.fetchBooks();
      if (this.props.showToast) {
        this.props.showToast("Book title deleted successfully!", "success");
      }
    } catch (err) {
      if (this.props.showToast) {
        this.props.showToast("Error deleting book: " + err.message, "error");
      }
    }
  };

  handleAddCopy = async (e) => {
    e.preventDefault();
    const { client } = this.props;
    const { selectedBook, newAccessionNo, newRackLocation, newCopyStatus } = this.state;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    if (!newAccessionNo.trim()) {
      this.setState({ modalError: "Accession Number is required!" });
      return;
    }

    try {
      this.setState({ modalError: null });
      await client.mutate({
        mutation: ADD_BOOK_COPY,
        variables: {
          book_id: selectedBook.id,
          accession_no: newAccessionNo.trim(),
          rack_location: newRackLocation.trim() || "General Shelf",
          status: newCopyStatus,
          institute_id: instId,
          branch_id: branchId
        }
      });
      
      this.setState({
        newAccessionNo: "",
        newRackLocation: "",
        newCopyStatus: "available"
      });
      await this.fetchBooks();
      const updatedBook = this.state.books.find(b => b.id === selectedBook.id);
      if (updatedBook) {
        this.setState({ selectedBook: updatedBook });
      }
      if (this.props.showToast) {
        this.props.showToast(`Physical copy "${newAccessionNo}" added successfully!`, "success");
      }
    } catch (err) {
      this.setState({ modalError: "Error adding copy: " + err.message });
    }
  };

  startEditingCopy = (copy) => {
    this.setState({
      editingCopyId: copy.id,
      editAccessionNo: copy.accession_no,
      editRackLocation: copy.rack_location || "",
      editCopyStatus: copy.status
    });
  };

  cancelEditingCopy = () => {
    this.setState({
      editingCopyId: null,
      editAccessionNo: "",
      editRackLocation: "",
      editCopyStatus: "available"
    });
  };

  handleUpdateCopy = async (copyId) => {
    const { client } = this.props;
    const { selectedBook, editAccessionNo, editRackLocation, editCopyStatus } = this.state;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    if (!editAccessionNo.trim()) {
      this.setState({ modalError: "Accession Number is required!" });
      return;
    }

    try {
      this.setState({ modalError: null });
      await client.mutate({
        mutation: UPDATE_BOOK_COPY,
        variables: {
          copy_id: copyId,
          accession_no: editAccessionNo.trim(),
          rack_location: editRackLocation.trim(),
          status: editCopyStatus,
          institute_id: instId,
          branch_id: branchId
        }
      });

      this.setState({ editingCopyId: null });
      await this.fetchBooks();
      const updatedBook = this.state.books.find(b => b.id === selectedBook.id);
      if (updatedBook) {
        this.setState({ selectedBook: updatedBook });
      }
      if (this.props.showToast) {
        this.props.showToast(`Physical copy updated successfully!`, "success");
      }
    } catch (err) {
      this.setState({ modalError: "Error updating copy: " + err.message });
    }
  };

  handleDeleteCopy = async (copyId) => {
    if (!window.confirm("Are you sure you want to delete this physical copy?")) return;
    const { client } = this.props;
    const { selectedBook } = this.state;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    try {
      this.setState({ modalError: null });
      await client.mutate({
        mutation: DELETE_BOOK_COPY,
        variables: { copy_id: copyId, institute_id: instId, branch_id: branchId }
      });
      await this.fetchBooks();
      const updatedBook = this.state.books.find(b => b.id === selectedBook.id);
      if (updatedBook) {
        this.setState({ selectedBook: updatedBook });
      }
      if (this.props.showToast) {
        this.props.showToast("Physical copy deleted successfully!", "success");
      }
    } catch (err) {
      this.setState({ modalError: "Error deleting copy: " + err.message });
    }
  };

  render() {
    const { books, searchQuery, showAddModal, showEditModal, showCopiesModal, selectedBook, loading, error, currentPage, pageSize, totalCount, modalError } = this.state;

    const totalPages = Math.ceil(totalCount / pageSize) || 1;
    const pageItems = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let p = startPage; p <= endPage; p++) {
      pageItems.push(
        <Pagination.Item 
          key={p} 
          active={p === currentPage} 
          onClick={() => this.handlePageChange(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    return (
      <div>
        {/* Subheader and Action buttons */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div style={{ flex: 1, maxWidth: 400 }}>
            <InputGroup>
              <InputGroup.Prepend>
                <InputGroup.Text style={{ background: "#fff", borderRight: "none", color: "#64748b" }}>
                  <i className="fal fa-search" />
                </InputGroup.Text>
              </InputGroup.Prepend>
              <FormControl
                placeholder="Search Title, Author, ISBN, Category..."
                name="searchQuery"
                value={searchQuery}
                onChange={this.handleFieldChange}
                style={{ borderLeft: "none", fontSize: 13 }}
              />
            </InputGroup>
          </div>
          <Button
            onClick={this.openAddModal}
            style={{
              background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
              border: "none",
              fontWeight: 700,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            <i className="fal fa-plus-circle" /> Add Book Title
          </Button>
        </div>

        {/* Catalog Table */}
        {loading ? (
          <div className="shimmer" style={{ height: 300, borderRadius: 12 }} />
        ) : error ? (
          <div className="p-4 text-center text-danger">Error: {error}</div>
        ) : books.length === 0 ? (
          <div className="text-center p-5 text-muted border rounded bg-white">
            <i className="fal pointer fa-book-open fa-3x d-block mb-3 text-light" />
            <p>No books found. Try adding a new book to the catalog.</p>
          </div>
        ) : (
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Table responsive hover className="mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#475569", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "12px 20px" }}>Book Details</th>
                  <th>ISBN</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th className="text-center">Total Copies</th>
                  <th className="text-center">Available</th>
                  <th className="text-center" style={{ width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {books.map((book) => (
                  <tr key={book.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "16px 20px" }}>
                      <div style={{ fontWeight: 800, color: "#1f2937", fontSize: 14 }}>{book.title}</div>
                      <small style={{ color: "#64748b" }}>by {book.author} ({book.language})</small>
                    </td>
                    <td>{book.isbn}</td>
                    <td>
                      <Badge variant="light" style={{ background: "#f0fdf4", color: "#166534", fontWeight: 600 }}>
                        {book.subject || "General"}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant="light" style={{ background: "#f1f5f9", color: "#475569", fontWeight: 600 }}>
                        {book.category}
                      </Badge>
                    </td>
                    <td style={{ fontWeight: 600, color: "#475569" }}>₹{book.price ? book.price.toFixed(2) : "0.00"}</td>
                    <td className="text-center" style={{ fontWeight: 600 }}>{book.total_copies}</td>
                    <td className="text-center">
                      <Badge
                        style={{
                          background: book.available_copies > 0 ? "#dcfce7" : "#fee2e2",
                          color: book.available_copies > 0 ? "#15803d" : "#ef4444",
                          borderRadius: 20,
                          padding: "4px 10px",
                          fontWeight: 700,
                        }}
                      >
                        {book.available_copies} available
                      </Badge>
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2" style={{ gap: 8 }}>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => this.openCopiesModal(book)}
                          style={{ color: "#0284c7", background: "#f0f9ff", border: "none", fontWeight: 700, fontSize: 11 }}
                        >
                          <i className="fal fa-copy" /> Copies ({book.copies ? book.copies.length : 0})
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => this.openEditModal(book)}
                          style={{ color: "#4f46e5", background: "#f5f3ff", border: "none" }}
                        >
                          <i className="fal fa-edit" />
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => this.handleDeleteBook(book.id)}
                          style={{ color: "#ef4444", background: "#fef2f2", border: "none" }}
                        >
                          <i className="fal fa-trash" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {/* Pagination Controls Dock */}
            <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-light border-top" style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Showing <strong>{totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}</strong> to <strong>{Math.min(currentPage * pageSize, totalCount)}</strong> of <strong>{totalCount}</strong> entries
              </div>
              
              <div className="d-flex align-items-center" style={{ gap: 16 }}>
                <Form.Control 
                  as="select" 
                  size="sm" 
                  value={pageSize} 
                  onChange={this.handlePageSizeChange} 
                  style={{ width: 100, fontSize: 12, height: 32, borderRadius: 6, border: "1.5px solid #e2e8f0" }}
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </Form.Control>

                <Pagination size="sm" className="mb-0">
                  <Pagination.First disabled={currentPage === 1} onClick={() => this.handlePageChange(1)} />
                  <Pagination.Prev disabled={currentPage === 1} onClick={() => this.handlePageChange(currentPage - 1)} />
                  {pageItems}
                  <Pagination.Next disabled={currentPage >= totalPages} onClick={() => this.handlePageChange(currentPage + 1)} />
                  <Pagination.Last disabled={currentPage >= totalPages} onClick={() => this.handlePageChange(totalPages)} />
                </Pagination>
              </div>
            </div>
          </div>
        )}

        {/* Add Book Modal */}
        <Modal show={showAddModal} onHide={() => this.setState({ showAddModal: false })} centered>
          <Form onSubmit={this.handleAddBookSubmit}>
            <Modal.Header closeButton style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <Modal.Title style={{ fontSize: 16, fontWeight: 800 }}>Add New Book to Catalog</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: "24px" }}>
              {modalError && (
                <Alert variant="danger" dismissible onClose={() => this.setState({ modalError: null })}>
                  {modalError}
                </Alert>
              )}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Book Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={this.state.title}
                  onChange={this.handleFieldChange}
                  required
                  placeholder="Enter book title"
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Author Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="author"
                      value={this.state.author}
                      onChange={this.handleFieldChange}
                      required
                      placeholder="Enter author"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>ISBN Code *</Form.Label>
                    <Form.Control
                      type="text"
                      name="isbn"
                      value={this.state.isbn}
                      onChange={this.handleFieldChange}
                      required
                      placeholder="Enter ISBN"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Subject *</Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={this.state.subject}
                      onChange={this.handleFieldChange}
                      required
                      placeholder="e.g. Mathematics"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Category *</Form.Label>
                    <Form.Control
                      type="text"
                      name="category"
                      value={this.state.category}
                      onChange={this.handleFieldChange}
                      required
                      placeholder="e.g. Computer Science"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Language</Form.Label>
                    <Form.Control
                      type="text"
                      name="language"
                      value={this.state.language}
                      onChange={this.handleFieldChange}
                      placeholder="e.g. English"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Price (INR) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      name="price"
                      value={this.state.price}
                      onChange={this.handleNumberChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Initial Physical Copies *</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      name="total_copies"
                      value={this.state.total_copies}
                      onChange={this.handleNumberChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Rack / Shelf Location</Form.Label>
                    <Form.Control
                      type="text"
                      name="rack_location"
                      value={this.state.rack_location}
                      onChange={this.handleFieldChange}
                      placeholder="e.g. General Rack, Shelf 1"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
              <Button variant="light" onClick={() => this.setState({ showAddModal: false })} style={{ fontWeight: 600 }}>
                Cancel
              </Button>
              <Button type="submit" style={{ background: "#6366f1", border: "none", fontWeight: 700 }}>
                Save Book
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Edit Book Modal */}
        <Modal show={showEditModal} onHide={() => this.setState({ showEditModal: false, selectedBook: null })} centered>
          <Form onSubmit={this.handleEditBookSubmit}>
            <Modal.Header closeButton style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <Modal.Title style={{ fontSize: 16, fontWeight: 800 }}>Edit Catalog Book</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: "24px" }}>
              {modalError && (
                <Alert variant="danger" dismissible onClose={() => this.setState({ modalError: null })}>
                  {modalError}
                </Alert>
              )}
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Book Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={this.state.title}
                  onChange={this.handleFieldChange}
                  required
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Author Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="author"
                      value={this.state.author}
                      onChange={this.handleFieldChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>ISBN Code *</Form.Label>
                    <Form.Control
                      type="text"
                      name="isbn"
                      value={this.state.isbn}
                      onChange={this.handleFieldChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Subject *</Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={this.state.subject}
                      onChange={this.handleFieldChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Category *</Form.Label>
                    <Form.Control
                      type="text"
                      name="category"
                      value={this.state.category}
                      onChange={this.handleFieldChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Language</Form.Label>
                    <Form.Control
                      type="text"
                      name="language"
                      value={this.state.language}
                      onChange={this.handleFieldChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Price (INR) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      name="price"
                      value={this.state.price}
                      onChange={this.handleNumberChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
              <Button variant="light" onClick={() => this.setState({ showEditModal: false, selectedBook: null })} style={{ fontWeight: 600 }}>
                Cancel
              </Button>
              <Button type="submit" style={{ background: "#6366f1", border: "none", fontWeight: 700 }}>
                Update Book
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Physical Copies Management Modal */}
        <Modal show={showCopiesModal} onHide={() => this.setState({ showCopiesModal: false, selectedBook: null })} size="lg" centered>
          <Modal.Header closeButton style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Modal.Title style={{ fontSize: 16, fontWeight: 800 }}>
              Physical Copies: {selectedBook ? selectedBook.title : ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "24px" }}>
            {modalError && (
              <Alert variant="danger" dismissible onClose={() => this.setState({ modalError: null })}>
                {modalError}
              </Alert>
            )}
            {selectedBook && (
              <div>
                {/* Form to add a new copy */}
                <Form onSubmit={this.handleAddCopy} className="mb-4 p-3 border rounded bg-light">
                  <h6 style={{ fontWeight: 700, fontSize: 13, color: "#475569" }} className="mb-3">Add New Physical Copy</h6>
                  <Row className="align-items-end">
                    <Col md={4}>
                      <Form.Group className="mb-0">
                        <Form.Label style={{ fontSize: 11, fontWeight: 700 }}>Accession Number *</Form.Label>
                        <Form.Control
                          type="text"
                          name="newAccessionNo"
                          value={this.state.newAccessionNo}
                          onChange={this.handleFieldChange}
                          placeholder="e.g. ACC-1002"
                          required
                          style={{ height: 35, fontSize: 12 }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-0">
                        <Form.Label style={{ fontSize: 11, fontWeight: 700 }}>Rack/Shelf Location</Form.Label>
                        <Form.Control
                          type="text"
                          name="newRackLocation"
                          value={this.state.newRackLocation}
                          onChange={this.handleFieldChange}
                          placeholder="e.g. Rack A, Shelf 3"
                          style={{ height: 35, fontSize: 12 }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-0">
                        <Form.Label style={{ fontSize: 11, fontWeight: 700 }}>Copy Status</Form.Label>
                        <Form.Control
                          as="select"
                          name="newCopyStatus"
                          value={this.state.newCopyStatus}
                          onChange={this.handleFieldChange}
                          style={{ height: 35, fontSize: 12, padding: "0 10px" }}
                        >
                          <option value="available">Available</option>
                          <option value="reference_only">Reference Only</option>
                          <option value="damaged">Damaged</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Button
                        type="submit"
                        style={{
                          background: "#0284c7",
                          border: "none",
                          fontWeight: 700,
                          fontSize: 12,
                          height: 35,
                          width: "100%"
                        }}
                      >
                        Add Copy
                      </Button>
                    </Col>
                  </Row>
                </Form>

                {/* Physical inventory copies list table */}
                <h6 style={{ fontWeight: 700, fontSize: 13, color: "#475569" }} className="mb-3">Inventory Copy List</h6>
                {!selectedBook.copies || selectedBook.copies.length === 0 ? (
                  <div className="text-center p-4 text-muted border rounded bg-white">
                    No physical copy registered for this title. Add a copy above.
                  </div>
                ) : (
                  <Table responsive bordered className="mb-0" style={{ fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", color: "#475569" }}>
                        <th>Accession Number</th>
                        <th>Rack Location</th>
                        <th>Status</th>
                        <th className="text-center" style={{ width: 100 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBook.copies.map((copy) => {
                        const isEditing = this.state.editingCopyId === copy.id;
                        return (
                          <tr key={copy.id}>
                            {isEditing ? (
                              <>
                                <td>
                                  <Form.Control
                                    type="text"
                                    name="editAccessionNo"
                                    value={this.state.editAccessionNo}
                                    onChange={this.handleFieldChange}
                                    style={{ height: 30, fontSize: 12 }}
                                    disabled={copy.status === "issued"}
                                    placeholder="Accession No"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="text"
                                    name="editRackLocation"
                                    value={this.state.editRackLocation}
                                    onChange={this.handleFieldChange}
                                    style={{ height: 30, fontSize: 12 }}
                                    placeholder="Rack Location"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    as="select"
                                    name="editCopyStatus"
                                    value={this.state.editCopyStatus}
                                    onChange={this.handleFieldChange}
                                    style={{ height: 30, fontSize: 12, padding: "0 5px" }}
                                    disabled={copy.status === "issued"}
                                  >
                                    <option value="available">Available</option>
                                    <option value="reference_only">Reference Only</option>
                                    <option value="damaged">Damaged</option>
                                    <option value="lost">Lost</option>
                                  </Form.Control>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex justify-content-center gap-1" style={{ gap: 4 }}>
                                    <Button
                                      variant="success"
                                      size="sm"
                                      onClick={() => this.handleUpdateCopy(copy.id)}
                                      style={{ padding: "2px 6px", fontSize: 10 }}
                                    >
                                      <i className="fal fa-check" />
                                    </Button>
                                    <Button
                                      variant="light"
                                      size="sm"
                                      onClick={this.cancelEditingCopy}
                                      style={{ padding: "2px 6px", fontSize: 10 }}
                                    >
                                      <i className="fal fa-times" />
                                    </Button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td style={{ fontWeight: 700, color: "#0f172a" }}>{copy.accession_no}</td>
                                <td>{copy.rack_location}</td>
                                <td>
                                  <Badge
                                    style={{
                                      background:
                                        copy.status === "available" ? "#dcfce7" :
                                        copy.status === "issued" ? "#e0f2fe" :
                                        copy.status === "reference_only" ? "#fef3c7" : "#fee2e2",
                                      color:
                                        copy.status === "available" ? "#15803d" :
                                        copy.status === "issued" ? "#0369a1" :
                                        copy.status === "reference_only" ? "#b45309" : "#ef4444",
                                      textTransform: "capitalize",
                                      padding: "4px 8px",
                                      borderRadius: 4,
                                      fontWeight: 700
                                    }}
                                  >
                                    {copy.status.replace("_", " ")}
                                  </Badge>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex justify-content-center gap-1" style={{ gap: 4 }}>
                                    <Button
                                      variant="light"
                                      size="sm"
                                      onClick={() => this.startEditingCopy(copy)}
                                      style={{ color: "#4f46e5", background: "#f5f3ff", border: "none", padding: "2px 6px" }}
                                    >
                                      <i className="fal fa-edit" />
                                    </Button>
                                    <Button
                                      variant="light"
                                      size="sm"
                                      onClick={() => this.handleDeleteCopy(copy.id)}
                                      disabled={copy.status === "issued"}
                                      style={{ color: "#ef4444", background: "#fef2f2", border: "none", padding: "2px 6px" }}
                                    >
                                      <i className="fal fa-trash-alt" />
                                    </Button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
            <Button variant="secondary" onClick={() => this.setState({ showCopiesModal: false, selectedBook: null })} style={{ fontWeight: 600 }}>
              Close Panel
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default BookCatalog;
